import { createContext, useContext, useMemo, useState } from "react";

type SlotState = {
  node: HTMLElement | null;
  setNode: (el: HTMLElement | null) => void;
};

interface PortalSlotsValue {
  footer: SlotState;
  headerActions: SlotState;
}

const PortalSlotsContext = createContext<PortalSlotsValue | null>(null);

export function PortalSlotsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [footerNode, setFooterNode] = useState<HTMLElement | null>(null);
  const [headerNode, setHeaderNode] = useState<HTMLElement | null>(null);

  const value = useMemo(
    () => ({
      footer: { node: footerNode, setNode: setFooterNode },
      headerActions: { node: headerNode, setNode: setHeaderNode },
    }),
    [footerNode, headerNode],
  );

  return <PortalSlotsContext value={value}>{children}</PortalSlotsContext>;
}

function useSlots(): PortalSlotsValue {
  const ctx = useContext(PortalSlotsContext);
  if (!ctx)
    throw new Error(
      "Portal slot hooks must be used within PortalSlotsProvider",
    );
  return ctx;
}

/** Returns the footer container DOM element (or null before mount). */
export function useFooterNode() {
  return useSlots().footer.node;
}

/** Returns a callback ref to attach to the footer slot div. */
export function useFooterSlotRef() {
  return useSlots().footer.setNode;
}

/** Returns a callback ref to attach to the header actions slot div. */
export function useHeaderActionsSlotRef() {
  return useSlots().headerActions.setNode;
}

/** Returns the header actions container DOM element (or null before mount). */
export function useHeaderActionsNode() {
  return useSlots().headerActions.node;
}
